import { schema } from '@mesa/core/zero';
import { Row } from '@rocicorp/zero';
import {
  Loader2Icon,
  GitMergeIcon as MergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
} from 'lucide-react';
import { useCallback } from 'react';


import { SplitButton, SplitButtonPrimary, SplitButtonSecondary, SplitButtonItem } from '@/components/ui/split-button';
import { useMergeStrategies } from '@/features/pulls/hooks/use-merge-strategies';
import { MergeStrategy } from '@/features/pulls/services/pulls';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';

type PullRecordRow = Row<typeof schema.tables.PullsTable>;

export interface MergeButtonProps {
  openMergeDialog: (strategy: MergeStrategy) => void;
  readyToMerge: boolean;
  pull: PullRecordRow;
  isAuthor: boolean;
}

export function MergeButton({ openMergeDialog, readyToMerge, pull, isAuthor }: MergeButtonProps) {
  const { defaultStrategy, repository } = useMergeStrategies();
  const z = useZero();

  const handleClose = useCallback(() => {
    z.mutate.PullsTable.openOrClose({
      nodeId: pull.nodeId,
      owner: pull.owner,
      repo: pull.repo,
      number: pull.number,
      event: 'CLOSE',
    });
  }, [z, pull]);

  const handleConvertToDraft = useCallback(() => {
    z.mutate.PullsTable.convertToDraft({
      nodeId: pull.nodeId,
      owner: pull.owner,
      repo: pull.repo,
      number: pull.number,
    });
  }, [z, pull]);

  return (
    <SplitButton variant={readyToMerge ? 'default' : 'outline'}>
      <SplitButtonPrimary
        onClick={() => openMergeDialog(defaultStrategy)}
        disabled={!repository}
        variant={readyToMerge ? 'default' : 'outline'}
        className={cn(
          readyToMerge &&
            'bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-500 hover:border-emerald-600 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50'
        )}
      >
        {repository ? (
          <>
            <MergeIcon className="size-4 text-indigo-700" />
            Merge
          </>
        ) : (
          <Loader2Icon className="size-4 animate-spin" />
        )}
      </SplitButtonPrimary>
      <SplitButtonSecondary
        variant={readyToMerge ? 'default' : 'outline'}
        className={cn(
          readyToMerge && 'bg-emerald-500 hover:bg-emerald-600 border border-emerald-500 hover:border-emerald-600'
        )}
      >
        {isAuthor && (
          <SplitButtonItem onClick={handleConvertToDraft}>
            <GitPullRequestDraftIcon className="size-4 text-gray-600" />
            Convert to Draft
          </SplitButtonItem>
        )}
        <SplitButtonItem onClick={handleClose}>
          <GitPullRequestClosedIcon className="size-4 text-red-600" />
          Close
        </SplitButtonItem>
      </SplitButtonSecondary>
    </SplitButton>
  );
}
